import React from 'react';
import { JcCommonProps, JcUtils } from '../utils/JcUtils';
import { JcButtonAppearance, jcButtonAppearanceClasses } from './button/JcButton';


export interface JcLinkProps extends JcCommonProps, JcButtonAppearance {
  readonly label: React.ReactNode;
  readonly href: string;
  readonly target?: React.HTMLAttributeAnchorTarget;
  readonly rel?: string;
  /** Accessible name when the visible label isn't descriptive (e.g. an abbreviation or icon). */
  readonly ariaLabel?: string;
  readonly onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Strips the default link styling (blue color + underline). Use when the
   * link wraps a card, logo, or other element that provides its own visuals
   * — the anchor still gives you correct semantics and middle-click support.
   * Ignored when `buttonType` is set (the link then looks like a button).
   */
  readonly unstyled?: boolean;
}


export const JcLink: React.FunctionComponent<JcLinkProps> = (props: JcLinkProps) => {

  // A link carrying a `buttonType` renders with the shared button appearance, so
  // a navigation can look exactly like a button while staying a real anchor.
  const asButton = props.buttonType !== undefined;
  const disabled = props.disabled ?? false;

  const appearanceClasses = asButton
    ? jcButtonAppearanceClasses(props, !disabled)
    : [props.unstyled ? "jc-link-unstyled" : "jc-link"];

  const commonProps = JcUtils.commonProps(props, appearanceClasses);
  const rel = props.rel ?? (props.target === "_blank" ? "noopener noreferrer" : undefined);

  // An <a> can't be natively disabled. Drop the href so it leaves the tab order
  // and can't be activated, expose the state to assistive tech, and swallow clicks.
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    props.onClick?.(event);
  };

  return <a
    {...commonProps}
    href={disabled ? undefined : props.href}
    target={props.target}
    rel={rel}
    onClick={handleClick}
    aria-label={props.ariaLabel}
    aria-disabled={disabled || undefined}
  >
    {props.label}
  </a>
}
